import { type LocaleDefinition } from '@solinkcorp/locale';
import filter from 'lodash/filter';
import find from 'lodash/find';
import flow from 'lodash/flow';
import isEmpty from 'lodash/isEmpty';
import { type VideoDiscoverySite } from 'utils/types';
import * as constants from './constants';
import {
  type CsvDataEntry,
  type CsvImportError,
  type LocationTag,
  type TagBase,
  type TagFilter,
  type TreeNode,
  type TreeTagNode,
} from './types';

/**
 * returns sorted tree strucutre for Tree component
 */
export function parseLocationsForTree(locations: VideoDiscoverySite[], locale: LocaleDefinition) {
  // root for all nodes
  const masterRootTreeNode = {
    id: constants.TREE_MASTER_ROOT_ID,
    parentId: '',
    label: locale.locationTagging.allTags,
    count: 0,
  };

  if (!locations.length || !doLocationsHaveTags(locations))
    return createTreeNodes([masterRootTreeNode]);

  const tagPathRelationships = flow(
    getUniqueTagPaths,
    createPathsWithHeaders,
    getTagPathsRelationships,
    addLabelToRelationships,
  )(locations.filter(({ locationTags }) => locationTags && locationTags?.length));

  const tagNodes = calculateTagCounts(locations, tagPathRelationships);

  const tree = createTreeNodes([{ ...masterRootTreeNode, count: locations.length }, ...tagNodes]);

  return sortTreeChildren(tree.filter((root) => !isEmpty(root.children)));
}

/**
 * takes in an array of unique (by id) TagNodes and returns tree structure for Tree component
 */
export function createTreeNodes<T extends TagBase>(
  dataset: readonly T[],
  sort: boolean | undefined = true,
): (T & { children: TreeNode[] })[] {
  const hashTable = Object.create(null);

  dataset.forEach((data) => {
    hashTable[data.id] = { ...data, children: [] };
  });

  const dataTree: (T & { children: TreeNode[] })[] = [];

  dataset.forEach((data) => {
    if (data.parentId) {
      hashTable[data.parentId]?.children.push(hashTable[data.id]);
    } else {
      dataTree.push(hashTable[data.id]);
    }
  });
  return sort ? dataTree.sort(sortTreeNodes) : dataTree;
}

/**
 * Every location has tag paths that look like:
 *  ['zone:1', 'zone:1/region:1']
 * Multiple locations can be tagged with the same zone, e.g "zone:1"
 * we need unique tag paths for creating tree nodes
 */
export function getUniqueTagPaths(locations: VideoDiscoverySite[]): string[] {
  // map of all unique tags for every location accessible by the user
  const uniquePaths = new Set<string>();

  locations.forEach((location) => {
    if (!location.locationTags || !location.locationTags.length) return;
    location.locationTags.forEach((tag: string) => {
      if (!uniquePaths.has(tag)) {
        uniquePaths.add(tag);
      }
    });
  });

  return Array.from(uniquePaths).sort();
}

/**
 * This function gets an array of tag paths ['zone:1', 'zone:1/region:1', 'zone:1/region:2']
 * and returns an array with header stings as follows
 * ['zone:HEADER', 'zone:1', 'zone:1/region:HEADER', 'zone:1/region:1', 'zone:1/region:1']
 * note: regions 1 and 2 have similar paths, zone:1, as such they will be under a common header node
 * 'zone:1/region:HEADER'
 */
function createPathsWithHeaders(tagPaths: string[]) {
  const tagPathHeaders = new Set<string>();

  tagPaths.forEach((tagPath) => {
    const tagPathHeader = tagPath.replace(
      constants.MATCH_EVERYTHING_AFTER_LAST_COLON_REGEX,
      constants.TREE_NODE_HEADER_SUFFIX,
    );

    if (!tagPathHeaders.has(tagPathHeader)) {
      tagPathHeaders.add(tagPathHeader);
    }
  });

  return [...tagPaths, ...Array.from(tagPathHeaders)];
}

/**
 * Create parent/child relationships given an array of strings from getUniqeTagPathsFromLocations()
 */
export function getTagPathsRelationships(tagPaths: string[]): { id: string; parentId: string }[] {
  return tagPaths.map((tagPath) => {
    const tagParts = tagPath.split(constants.TAGS_SEPARATOR);

    // case: tagPath is a direct child of TREE_MASTER_ROOT_ID
    // "zone:1", "brand:gap"
    const isTagPathRootHeader =
      tagParts.length === 1 && tagPath.includes(constants.TREE_NODE_HEADER_SUFFIX);
    if (isTagPathRootHeader) {
      return {
        id: tagPath,
        parentId: constants.TREE_MASTER_ROOT_ID,
      };
    }

    // case: tag path has no header suffix
    // zone:1, parent is zone:HEADER
    const isTagPathRoot = tagParts.length === 1;
    if (isTagPathRoot) {
      const parentId = tagPath.replace(
        constants.MATCH_EVERYTHING_AFTER_LAST_COLON_REGEX,
        constants.TREE_NODE_HEADER_SUFFIX,
      );
      return {
        id: tagParts[0],
        parentId,
      };
    }

    // case: tagPath has header suffix
    // zone:1/region:HEADER, parent is zone:1
    if (tagPath.includes(constants.TREE_NODE_HEADER_SUFFIX)) {
      return {
        id: tagPath,
        parentId: tagParts.slice(0, tagParts.length - 1).join(constants.TAGS_SEPARATOR),
      };
    }

    // case: zone:1/region:1, parent is zone:1/region:HEADER
    return {
      id: tagPath,
      parentId: tagPath.replace(
        constants.MATCH_EVERYTHING_AFTER_LAST_COLON_REGEX,
        constants.TREE_NODE_HEADER_SUFFIX,
      ),
    };
  });
}

/**
 * {[tag_path]:  frequency_count}[]
 */
export function calculateTagCounts(
  locations: any[],
  nodesWithoutCount: Omit<TreeTagNode, 'count'>[],
): TreeTagNode[] {
  return nodesWithoutCount.map((node): TreeTagNode => {
    if (node.id.includes(constants.TREE_NODE_HEADER_SUFFIX)) {
      return { ...node, count: undefined };
    }

    const tagCount = locations.filter((location) => {
      if (!location.locationTags) return false;
      return location.locationTags.includes(node.id);
    }).length;
    return { ...node, count: tagCount };
  });
}

function addLabelToRelationships(
  tagPathRelationships: { id: string; parentId: string }[],
): { id: string; parentId: string; label: string }[] {
  return tagPathRelationships.map((relationship) => {
    const tagParts = relationship.id.split(constants.TAGS_SEPARATOR);
    // zone:1/region:HEADER, label = 'region`
    // zone:1/region:1, label = 1
    const label = tagParts[tagParts.length - 1].split(constants.TAGS_VALUE_SEPARATOR)[
      relationship.id.includes(constants.TREE_NODE_HEADER_SUFFIX)
        ? constants.CATEGORY_INDEX
        : constants.VALUE_INDEX
    ];
    return {
      ...relationship,
      label,
    };
  });
}

// ['zone:1', 'region:1']
function formatTagLabel(tagParts: string[]) {
  const lastDescendantTag = tagParts[tagParts.length - 1].split(constants.TAGS_VALUE_SEPARATOR);

  return lastDescendantTag.join(' ');
}

// 'zone:1/region:1/district:1' => 'district 1'
export const formatTableTag = (tag: string) => {
  return formatTagLabel(tag.split(constants.TAGS_SEPARATOR));
};

/*
 * This function takes a tag in the following format saved on the location
 * CUSTOMER_ID:TAG_CATEGORY:TAG_VALUE/CUSTOMER_ID:TAG_CATEGORY:TAG_VALUE
 * It parses the tag into an object with an id, label, parentId and value
 * { id: TAG_CATEGORY, label: TAG_CATEGORY, parentId: PARENT_TAG_CATEGORY, value: TAG_VALUE }
 */
export const getTagInfo = (tag: string) => {
  const tagParts = tag.split(constants.TAGS_SEPARATOR);
  const [category, value] = tagParts[tagParts.length - 1].split(constants.TAGS_VALUE_SEPARATOR);
  const parentId =
    tagParts.length > 1
      ? tagParts[tagParts.length - 2].split(constants.TAGS_VALUE_SEPARATOR)[0]
      : '';
  return { id: category, label: category, parentId, value };
};

/*
 * Takes in an array of location tags { id: TAG_CATEGORY, label: TAG_CATEGORY, parentId: PARENT_TAG_CATEGORY, value: TAG_VALUE }
 * It constructs an array of strings, where each string has the tag path
 * CUSTOMER_ID:TAG_CATEGORY:TAG_VALUE/CUSTOMER_ID:TAG_CATEGORY:TAG_VALUE
 */
export const constructTagPaths = (tags: LocationTag[]) => {
  const newTags: string[] = [];

  const getTag = (tag: LocationTag, path = '') => {
    const parentTag = tags.find((currTag) => currTag.id === tag.parentId);
    let accPath = `${tag.id}:${tag.value}` + path;
    if (parentTag) accPath = '/' + accPath;
    if (!parentTag) {
      newTags.push(accPath);
      return;
    }
    getTag(parentTag, accPath);
  };
  tags.forEach((tag) => {
    getTag(tag);
  });

  return newTags;
};

/** Replace one tag value. A new parent value clears descendant values. */
export const applyTagValue = (tags: LocationTag[], id: string, tagValue: string): LocationTag[] => {
  const targetTag = tags.find((tag) => tag.id === id);
  const valueHasChanged = targetTag?.value !== tagValue;
  const childrenTags = getChildrenTags(id, tags);

  return tags.map((tag) => {
    if (tag.id === id) return { ...tag, value: tagValue };
    if (valueHasChanged && childrenTags.some((child) => child.id === tag.id)) {
      return { ...tag, value: '' };
    }
    return tag;
  });
};

const tagInputIsDisabled = (tags: LocationTag[], tag: LocationTag) =>
  !!tag.parentId && !tags.find((item) => item.id === tag.parentId)?.value;

/**
 * Apply typed field text onto tags.
 * A parent change clears descendants unless that field has its own text.
 * Text that contains tagging special characters stays unchanged.
 */
export const commitTagDrafts = (
  tags: LocationTag[],
  drafts: Readonly<Record<string, string>>,
): LocationTag[] =>
  tags.reduce((next, tag) => {
    const draft = drafts[tag.id];
    if (draft === undefined || draft === tag.value) return next;
    if (constants.REGEX_INCLUDES_LOCATION_TAGGING_SPECIAL_CHARACTERS.test(draft)) return next;

    const current = next.find((item) => item.id === tag.id);
    if (!current || tagInputIsDisabled(next, current)) return next;
    return applyTagValue(next, tag.id, draft);
  }, tags);

/*
 * Takes an id and an array of tags
 * { id: TAG_CATEGORY, label: TAG_CATEGORY, parentId: PARENT_TAG_CATEGORY, value: TAG_VALUE }[]
 * Returns all of the tags that are children of target tag
 */
export const getChildrenTags = (tagId: string | undefined, tags: LocationTag[]) => {
  if (!tagId) return [];
  const children: LocationTag[] = [];
  const getChild = (id: string) => {
    const child = tags.find((tag) => tag.parentId === id);
    if (child) {
      children.push(child);
      getChild(child.id);
    }
  };
  getChild(tagId);
  return children;
};

/*
 * Filters locations based on array of filters
 * Filters on location address, name, and tags and is case insensitive
 */
export const filterLocations = (locations: VideoDiscoverySite[], filters: { value: string }[]) => {
  const searchTerms = filters.map((f) => f.value.toLowerCase().trim()).filter(Boolean);

  return filter(locations, (location) => {
    let testString = '';
    if (location.locationTags) {
      location.locationTags.forEach((tag: string) => {
        testString += tag.split('/').reduce((acc, current) => {
          const tagParts = current.split(':');
          return `${acc} ${tagParts[0]} ${tagParts[1]}`;
        }, '');
      });
    }

    testString += `${location.address} ${location.name}`;
    testString = testString.toLowerCase();

    return searchTerms.every((term) => testString.includes(term));
  });
};

// TODO: waiting on hierarchy api
export function parseCategoriesIntoTagHierarchy() {
  return [];
}

/*
 * Create a map of all of the different tag paths using the location tags
 * For e.g.
 * { 'parentCategory': [
    {
      id: 'parentCategory',
      value: 'parentValue',
      label: 'parentValue',
      parentId: '',
    },
  ],
  'parentCategory:parentValue/category': [
    {
      id: 'category',
      value: 'valueOne',
      label: 'valueOne',
      parentId: 'parentCategory',
    },
  ]}
 */
export const createTagsMap = (locations: VideoDiscoverySite[]) => {
  const tagsMap: { [key: string]: LocationTag[] } = {};
  locations.forEach((location) => {
    location.locationTags?.forEach((tag) => {
      const tagParts = tag.split(constants.TAGS_VALUE_SEPARATOR);

      const tagValue = tagParts.pop();
      const tagKey = tagParts.join(constants.TAGS_VALUE_SEPARATOR);
      const categoryParts = tagKey.split(constants.TAGS_SEPARATOR);
      const category = categoryParts[categoryParts.length - 1];
      const parentCategory =
        categoryParts[categoryParts.length - 2]?.split(constants.TAGS_VALUE_SEPARATOR)[0] || '';
      if (tagValue && tagsMap[tagKey] && !tagsMap[tagKey].find((tag) => tag.value === tagValue)) {
        tagsMap[tagKey].push({
          id: category,
          value: tagValue,
          label: tagValue,
          parentId: parentCategory,
        });
      } else if (tagValue && !tagsMap[tagKey]) {
        tagsMap[tagKey] = [
          { id: category, value: tagValue, label: tagValue, parentId: parentCategory },
        ];
      }
    });
  });
  return tagsMap;
};

/*
 * Takes in an array of tags as well as the targetTag
 * It generates the key that is necessary for the tagsMap lookup table
 */
export const getTagKeyForTagsMap = (tags: LocationTag[], targetTag: LocationTag | undefined) => {
  if (!targetTag) return '';

  let tagKey = `${targetTag.label}`;

  const appendParentTag = (id: string) => {
    const parentTag = tags.find((tag) => tag.id === id);
    if (parentTag) {
      tagKey = `${parentTag.label}:${parentTag.value}/` + tagKey;
      appendParentTag(parentTag.parentId);
    }
  };

  appendParentTag(targetTag.parentId);
  return tagKey;
};

/*
 * Takes in a string that has periods in between the categories
 * For e.g. 'grandparent.parent.child'
 * Returns the parent tags ['grandparent.parent', 'grandparent']
 */
export const getParentCsvCategories = (category: string) => {
  const parents = [];
  for (let i = 0; i < category.length; i++) {
    if (category[i] === constants.TAGS_CATEGORY_CSV_SEPARATOR) {
      parents.push(category.substring(0, i));
    }
  }
  return parents;
};

/*
 * Creates a new list of locations which have their tags added using the csvData
 * It will ignore any locations that have errors
 */
export const getLocationsWithTags = (
  csvData: CsvDataEntry[],
  csvError: CsvImportError | null,
  locations: VideoDiscoverySite[],
) => {
  const locationsWithTags = locations.map((location) => {
    const csvEntry: CsvDataEntry | undefined = csvData.find(
      (csvEntry) =>
        location.id === csvEntry[constants.LOCATION_ID_HEADER] ||
        (location.externalId && location.externalId === csvEntry[constants.EXTERNAL_ID_HEADER]),
    );
    const locationHasError = csvError?.locations?.find(
      (errorLocation) => errorLocation.id === csvEntry?.[constants.LOCATION_ID_HEADER],
    );
    const newLocation = { id: location.id, locationTags: [] as string[], name: location.name };

    if (csvEntry) {
      for (const [key, value] of Object.entries(csvEntry)) {
        if (value && !constants.DEFAULT_HEADERS.includes(key) && !locationHasError) {
          const splitTags = key.split(constants.TAGS_CATEGORY_CSV_SEPARATOR);
          const childrenCategories = getParentCsvCategories(key);
          let formattedTag = '';

          splitTags.forEach((tag, index) => {
            let tagValue = '';
            if (index >= childrenCategories.length) {
              tagValue = value;
            } else {
              tagValue = csvEntry[childrenCategories[index]];
            }
            if (index === 0) {
              formattedTag = `${tag}:${tagValue}`;
            } else {
              formattedTag += `/${tag}:${tagValue}`;
            }
          });

          if (!newLocation.locationTags) {
            newLocation.locationTags = [formattedTag];
          } else {
            newLocation.locationTags.push(formattedTag);
          }
        }
      }
    }
    return newLocation;
  });

  return locationsWithTags;
};

// everything after this index is tag information
const SOLINK_CSV_HEADER_END_INDEX = 2;

/*
 * Returns an array of arrays
 * The first array is all of the table headers default headers as well as the tag categories
 * ['External ID', 'Location ID', 'Location', 'Zone', 'Zone.Region']
 * Subsequent arrays are all the locations with their externalId, locationId and name
 * ['locationId-xxx', 'externalId-xxx', 'locationName']
 */
export const getExportData = (data: TreeNode[], locations: VideoDiscoverySite[]) => {
  const tableHeaders = [...constants.DEFAULT_HEADERS];
  const addHeaders = (entry: TreeNode, prefix: string) => {
    const header = prefix ? `${prefix}.${entry.id}` : entry.id;
    tableHeaders.push(header);
    entry.children?.forEach((child) => addHeaders(child, header));
  };
  data.forEach((entry) => addHeaders(entry, ''));
  const exportData = [tableHeaders];

  const tagCategoriesArr = tableHeaders.slice(SOLINK_CSV_HEADER_END_INDEX + 1, tableHeaders.length);

  locations.forEach((location) => {
    const row = [location.externalId || '', location.id, location.name];

    // we skip to next iteration if there are no categories
    // or if the location has no tags
    if (!location.locationTags?.length || !tagCategoriesArr.length) {
      exportData.push(row);
      return;
    }

    const locationTagValues = getLocationTagsForCSV(location.locationTags, tagCategoriesArr);

    exportData.push(row.concat(locationTagValues));
  });
  return exportData;
};

/**
 * There are 3 default CSV header: external id, location id and location
 * tagCategoriesArr is an array of the category headers, e.g ["Zone", "Zone.Region"]
 * for each category header we'll get the value for each tag path
 * @param location string[]
 * @param tagCategoriesArr VideoDiscoveryLocation
 */
export function getLocationTagsForCSV(locationTags: string[], tagCategoriesArr: string[]) {
  const tagsCSVRow: string[] = [];

  // tagCategoriesArr = ["zone", "zone.region"]
  tagCategoriesArr.forEach((categoryPath) => {
    const category = categoryPath.split('.').pop();

    if (!category) {
      return;
    }

    let locationHasCategoryTagValue = false;

    for (const tag of locationTags) {
      if (tag.includes(category)) {
        locationHasCategoryTagValue = true;
        // zone:1/region:12 --> 12
        const tagValue = tag
          .split(constants.TAGS_SEPARATOR)
          .pop()
          ?.split(constants.TAGS_VALUE_SEPARATOR)[1];
        tagsCSVRow.push(tagValue as string);
        // we break out of this for loop and check if the location
        // has any tag values for the next category
        break;
      }
    }

    // if the loop reaches this point and no tag values match the
    // current category, we push an empty string to add an empty cell in the CSV
    if (!locationHasCategoryTagValue) {
      tagsCSVRow.push('');
    }
  });

  return tagsCSVRow;
}

/*
 * Validates the csv data
 * Three possible scenarions
 * 1. No Errors, returns null
 * 2. Minor error, returns { type: 'minor' , locations:[]}
 * 3. Major Error, returns { type: 'major' }
 */
export const validateCsvData = ({
  data,
  tagCategories,
  csvHeaders,
  locations,
}: {
  data: CsvDataEntry[];
  tagCategories: readonly LocationTag[];
  csvHeaders: string[];
  locations: VideoDiscoverySite[];
}): { error: CsvImportError | null; numberOfLocationsTagged: number } => {
  const majorError = checkForCsvMajorError(csvHeaders, tagCategories);

  if (majorError) return { error: majorError, numberOfLocationsTagged: 0 };
  const minorError = checkForCsvMinorErrors({ data, locations });
  const numberOfLocationsTagged = getNumberOfLocationsTagged({
    data,
    error: minorError,
    locations,
  });
  return { error: minorError, numberOfLocationsTagged };
};

/*
 * The CSV table headers must match the tag categories
 * If this is not the case, return a major error and locations will be tagged
 */
export const checkForCsvMajorError = (
  csvHeaders: string[],
  tagCategories: readonly LocationTag[],
): CsvImportError | null => {
  const majorError: CsvImportError = {
    type: constants.MAJOR_CSV_ERROR,
  };
  const csvTagCategories = csvHeaders
    .filter((header) => !constants.DEFAULT_HEADERS.includes(header))
    .map((header) => {
      const headerParts = header.split(constants.TAGS_CATEGORY_CSV_SEPARATOR);
      return headerParts[headerParts.length - 1];
    });

  if (tagCategories.length !== csvTagCategories.length) {
    return majorError;
  }
  for (const category of tagCategories) {
    if (!csvTagCategories.includes(category.id)) {
      return majorError;
    }
  }
  return null;
};

/*
 * There is a minor error if the following condition is true
 * 1. If a location has a child tag, without its parent
 * 2. There is a special character in the tag value
 * In the event of a minor error, the problematic locations will NOT be tagged
 */
export const checkForCsvMinorErrors = ({
  data,
  locations,
}: {
  data: CsvDataEntry[];
  locations: VideoDiscoverySite[];
}): CsvImportError | null => {
  const minorError: CsvImportError = {
    type: 'minor',
    locations: [],
  };
  let error: typeof minorError | null = null;

  data.forEach((dataEntry) => {
    const locationId = dataEntry[constants.LOCATION_ID_HEADER];
    const locationName = dataEntry[constants.LOCATION_HEADER];
    const externalId = dataEntry[constants.EXTERNAL_ID_HEADER];
    const hasAccessToLocation = !!locations.find(
      (location) =>
        location.id === dataEntry[constants.LOCATION_ID_HEADER] ||
        (location.externalId && location.externalId === dataEntry[constants.EXTERNAL_ID_HEADER]),
    );

    for (const [key, value] of Object.entries(dataEntry)) {
      const locationAlreadyExists = error?.locations?.find(
        (location) => location.id === locationId,
      );
      if (
        !hasAccessToLocation ||
        (!locationId && !externalId) ||
        (constants.REGEX_INCLUDES_LOCATION_TAGGING_SPECIAL_CHARACTERS.test(value) &&
          !constants.DEFAULT_HEADERS.includes(key))
      ) {
        if (!error) {
          error = minorError;
        }

        if (!locationAlreadyExists) {
          error?.locations?.push({ id: locationId, name: locationName, externalId });
        }
      }

      if (value && !constants.DEFAULT_HEADERS.includes(key)) {
        const categories = [...getParentCsvCategories(key), key];
        let tagPath = '';

        categories.forEach((category) => {
          const categoryParts = category.split(constants.TAGS_CATEGORY_CSV_SEPARATOR);
          const label = categoryParts[categoryParts.length - 1];
          const value = dataEntry[category];

          if (!value) {
            // Location has tag without its parent
            if (!error) {
              error = minorError;
            }

            if (!locationAlreadyExists) {
              error?.locations?.push({ id: locationId, name: locationName, externalId });
            }
          }
          tagPath += `${tagPath ? '.' : ''}${label}:${value}`;
        });
      }
    }
  });
  return error;
};

/*
 * Counts the number of locations that will be tagged from the csv data
 * It exludes any locations that have errors
 */
export const getNumberOfLocationsTagged = ({
  data,
  error,
  locations,
}: {
  data: CsvDataEntry[];
  error: CsvImportError | null;
  locations: VideoDiscoverySite[];
}) => {
  const locationsThatWillBeTagged = [];

  data.forEach((dataEntry) => {
    let locationHasTag = false;

    const locationHasError = error?.locations?.find(
      (location) =>
        location.id === dataEntry[constants.LOCATION_ID_HEADER] ||
        (location.externalId && location.externalId === dataEntry[constants.EXTERNAL_ID_HEADER]),
    );

    const locationExists = locations.find(
      (location) =>
        location.id === dataEntry[constants.LOCATION_ID_HEADER] ||
        (location.externalId && location.externalId === dataEntry[constants.EXTERNAL_ID_HEADER]),
    );

    for (const [key, value] of Object.entries(dataEntry)) {
      if (key && value && !constants.DEFAULT_HEADERS.includes(key)) {
        locationHasTag = true;
      }
    }

    if (!locationHasError && locationHasTag && locationExists) {
      locationsThatWillBeTagged.push({
        id: dataEntry[constants.LOCATION_ID_HEADER],
        name: dataEntry[constants.LOCATION_HEADER],
        externalId: dataEntry[constants.EXTERNAL_ID_HEADER],
      });
    }
  });
  return locationsThatWillBeTagged.length;
};

// receives locations filtered by search pills and applies filter from tree
export function filterLocationsForTable(locations: VideoDiscoverySite[], filter: string) {
  return locations.filter(({ locationTags }) => {
    if (!locationTags || locationTags.length === 0) return false;

    return !!find(locationTags, (tag) => tag.includes(filter));
  });
}

export const sortTagsMap = (tagsMap: { [key: string]: LocationTag[] } | null) => {
  if (!tagsMap) return null;
  const sortedTagsMap = { ...tagsMap };
  for (const [key, tags] of Object.entries(tagsMap)) {
    sortedTagsMap[key] = sortTags(tags);
  }
  return sortedTagsMap;
};

/*
 * Sorts location tags numerically if the value is a number
 * Otherwise the tags are sorted alphabetically
 */
export const sortTags = (tags: LocationTag[]) => {
  const sortedTags = tags?.sort((a, b) => {
    /* Sort numerically if the values are numbers*/
    if (!Number.isNaN(+a.value) && !Number.isNaN(+b.value)) {
      return +a.value - +b.value;
    }
    /* Sort alphabetically otherwise */
    const textA = a.value.toLowerCase();
    const textB = b.value.toLowerCase();
    return textA < textB ? -1 : textA > textB ? 1 : 0;
  });
  return sortedTags;
};

export function sortTreeChildren(nodes: TreeNode[]): TreeNode[] {
  const sortedNodes = nodes.map((node) => {
    if (node.children) {
      node.children = node.children.sort(sortTreeNodes);
      sortTreeChildren(node.children);
      return node;
    }

    return node;
  });

  return sortedNodes;
}

function sortTreeNodes(a: TagBase, b: TagBase) {
  /* Sort numerically if the values are numbers*/
  if (!Number.isNaN(+a.label) && !Number.isNaN(+b.label)) {
    return +a.label - +b.label;
  }
  /* Sort alphabetically otherwise */
  const textA = a.label.toLowerCase();
  const textB = b.label.toLowerCase();
  return textA < textB ? -1 : textA > textB ? 1 : 0;
}

/*
 * Creates an array of all location tagged with tag category
 */
export function findAllTaggedLocations(
  locations: VideoDiscoverySite[],
  tagCategory: TreeNode,
): VideoDiscoverySite[] {
  const taggedLocations: VideoDiscoverySite[] = [];
  locations.forEach((location) => {
    if (location.locationTags) {
      for (const tag of location.locationTags) {
        if (tag.includes(tagCategory.label)) {
          taggedLocations.push(location);
          break;
        }
      }
    }
  });
  return taggedLocations;
}

/*
 * Searches through all locations and checks if any locations have been tagged
 */
export function doLocationsHaveTags(locations: VideoDiscoverySite[]) {
  for (const location of locations) {
    if (Object.hasOwn(location, 'locationTags') && location.locationTags?.length !== 0) {
      return true;
    }
  }

  return false;
}

export function getLocationTags(location: VideoDiscoverySite | null) {
  return location?.locationTags?.map((tag) => getTagInfo(tag)) || [];
}

export const deleteTagsByCategory = (locations: VideoDiscoverySite[], category: string) => {
  return locations.map((location) => {
    return {
      id: location.id,
      name: location.name,
      locationTags: location.locationTags?.filter((tag) => !tag.includes(category)),
    };
  });
};

/**
 *
 * @param id for a node with "zone:1/region:1" we want to show
 * "region" has the header for all of the regions under zone:1
 */
export function getTreeNodeTitle(id: string) {
  const tagPaths = id.split(constants.TAGS_SEPARATOR);

  return tagPaths.slice(-1)[0].split(constants.TAGS_VALUE_SEPARATOR)[0];
}

/**
 * Checks to see if the list of locationTags has the target node's parent tag
 * Determines which parent is required based on the node's id. For e.g. 'Zone:Z1/Region:R2''s required parent is Zone:Z1
 * @param tagNode - TagFilter object, where id is the locationTag
 * @param locationTags - List of all the locationTags that are s For e.g ['Zone:Z1/Region:R2']
 */
export function hasParentNode(tagNode: TagFilter, locationTags: readonly string[]) {
  const allTags = tagNode.id.split('/');
  allTags.pop();
  const requiredParentId = allTags.length ? allTags.join('/') : undefined;
  return requiredParentId ? locationTags.includes(requiredParentId) : true;
}

/**
 * Checks to see if any nodes are included in the list of visibleTags
 * @param nodes - Array of TagFilter object of nodes
 * @param visibleTagsByIds - List of all the locationTags filters that are visible
 */
export function isAtleastOneChild(nodes: TagFilter[], visibleTagsByIds: readonly string[]) {
  for (const node of nodes) {
    const hasVisibleChild = node.type === 'tag' && visibleTagsByIds.includes(node.id);
    if (hasVisibleChild) {
      return true;
    }
  }
  return false;
}

/**
 * Groups the locationTaggingFilters by parentId
 * It will ignore ones that are not included in the selectedFiltersById
 * @param locationTaggingFilters - Array of TagFilter object of nodes
 * @param selectedFiltersById - List of all the locationTags filters that are selected
 */
export function groupLocationTagsByParentId({
  locationTaggingFilters,
  selectedFiltersById,
}: {
  locationTaggingFilters: TagFilter[];
  selectedFiltersById: string[];
}) {
  const selectedFiltersGroupedByParentId: { [key: string]: TagFilter[] } = {};

  locationTaggingFilters.forEach((filter) => {
    if (selectedFiltersById.includes(filter.id)) {
      if (selectedFiltersGroupedByParentId[filter.parentId]) {
        selectedFiltersGroupedByParentId[filter.parentId].push(filter);
      } else {
        selectedFiltersGroupedByParentId[filter.parentId] = [filter];
      }
    }
  });

  return selectedFiltersGroupedByParentId;
}

/**
 * Breaks apart a location tag and returns an array for all the tags that it's made up of
 * @param tagId - a location tag string
 */
export function getParentTagIdsAndTarget(tagId: string) {
  const tagIds: string[] = [];
  let tagString = '';
  const splitTag = tagId.split(constants.TAGS_SEPARATOR);
  splitTag.forEach((item: string) => {
    tagString += tagString ? `${constants.TAGS_SEPARATOR}${item}` : item;
    if (!tagIds.includes(tagString)) {
      tagIds.push(tagString);
    }
  });
  return tagIds;
}

/**
 * Breaks apart a location tag and returns the tag value
 * @param tag- a location tag string
 */
export function getLocationTagValue(tag: string) {
  return getLocationTagCategoryAndValue(tag)[constants.VALUE_INDEX];
}

/**
 * Breaks apart a location tag and returns the tag category
 * @param tag - a location tag string
 */
export function getLocationTagCategory(tag: string) {
  return getLocationTagCategoryAndValue(tag)[constants.CATEGORY_INDEX];
}

/**
 * Breaks apart a location tag and returns an array with the location category and tag in that order
 * @param tag- a location tag string
 */
export function getLocationTagCategoryAndValue(tag: string) {
  return (
    tag.split(constants.TAGS_SEPARATOR).pop()?.split(constants.TAGS_VALUE_SEPARATOR) || ['', '']
  );
}

/**
 * Breaks apart a location tag and returns the parent category
 * @param tag- a location tag string
 */
export function getParentCategory(tag: string) {
  const splitTags = tag.split(constants.TAGS_SEPARATOR);
  const parentCategoryParts = splitTags.length > 1 ? splitTags[splitTags.length - 2] : undefined;
  const parentCategory = parentCategoryParts
    ? parentCategoryParts.split(constants.TAGS_VALUE_SEPARATOR)[0]
    : '';
  return parentCategory;
}

/**
 * Breaks apart a location tag and returns how many levels deep a particular tag is
 * @param tag- a location tag string
 */
export function getLocationTagDepth(tag: string) {
  return tag.split(constants.TAGS_SEPARATOR).length - 1;
}

export function flatLocationTags(tags: Record<string, readonly string[]>): string[] {
  return Object.values(tags).flat();
}
